import { flushSync } from 'react-dom';

/**
 * Transições da tabela do estudo da Início (T7.29, etapa 4), pela View Transitions API do
 * próprio navegador (o WebView do Android a tem): o bloco da central cresce até virar a
 * página ("morph") e a troca de contexto esmaece ("ctx"). Sem a API, ou com movimento
 * reduzido, a troca acontece direto, sem nada pela metade.
 */
type Kind = 'morph' | 'ctx';

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void | Promise<void>) => { finished: Promise<void> };
};

/** A página nova já tem conteúdo (sem esqueleto de espera)? */
const pageReady = () => {
  const page = document.querySelector('.stg-page');
  return !!page && !page.querySelector('.sk');
};

/** A Início do outro contexto já desenhou alguma coisa (não ficou só o espaço vazio)? */
const contextReady = () => {
  const main = document.querySelector('.app-main');
  return !!main && (main.textContent ?? '').trim().length > 40 && !main.querySelector('.sk');
};

/**
 * Espera a página nova ficar pronta, no máximo `limit` ms: enquanto isso a tela antiga fica
 * parada (é o que a API mostra), e o crescimento acontece uma vez só, já com o conteúdo.
 */
const until = (ready: () => boolean, limit: number) =>
  new Promise<void>((resolve) => {
    const start = performance.now();
    const step = () => {
      if (ready() || performance.now() - start > limit) resolve();
      else setTimeout(step, 16);
    };
    step();
  });

const reduced = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
};

export function withTransition(kind: Kind, update: () => void, from?: HTMLElement | null): void {
  const doc = document as ViewTransitionDocument;
  if (!doc.startViewTransition || reduced()) {
    update();
    return;
  }
  const root = document.documentElement;
  root.dataset.vt = kind;
  if (from) from.style.setProperty('view-transition-name', 'vt-morph');
  try {
    const transition = doc.startViewTransition(async () => {
      if (from) from.style.removeProperty('view-transition-name');
      flushSync(update);
      if (kind === 'morph') await until(pageReady, 450);
      else await until(contextReady, 600);
    });
    void transition.finished.finally(() => {
      // A página que cresceu já está no lugar: a entrada de sempre (deslizar) não roda depois.
      if (kind === 'morph')
        document.querySelectorAll<HTMLElement>('.stg-page').forEach((page) => {
          page.style.animation = 'none';
        });
      delete root.dataset.vt;
    });
  } catch {
    if (from) from.style.removeProperty('view-transition-name');
    delete root.dataset.vt;
    update();
  }
}
